// DESCRIPTION: Block Edit component for nemtly/event-calendar — shows a visual preview
// and inspector controls for configuring view, filters, and taxonomy pre-filtering.

import React, { useState, useEffect } from 'react';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	ToggleControl,
	CheckboxControl,
	Spinner,
	TextControl,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { decodeEntities } from '@wordpress/html-entities';
import ServerSideRender from '@wordpress/server-side-render';

const VIEW_OPTIONS = [
	{ label: __( 'Month', 'nemtly-booking' ), value: 'month' },
	{ label: __( 'Week', 'nemtly-booking' ), value: 'week' },
	{ label: __( 'Day', 'nemtly-booking' ), value: 'day' },
	{ label: __( 'List', 'nemtly-booking' ), value: 'list' },
];

const ALL_VIEWS = [ 'month', 'week', 'day', 'list' ];

const EVENT_TYPE_OPTIONS = [
	{ label: __( 'All types', 'nemtly-booking' ), value: '' },
	{ label: __( 'One-on-One', 'nemtly-booking' ), value: 'one-on-one' },
	{ label: __( 'Group', 'nemtly-booking' ), value: 'group' },
	{ label: __( 'Event', 'nemtly-booking' ), value: 'event' },
];

const VIEW_LABELS = {
	month: __( 'Month', 'nemtly-booking' ),
	week: __( 'Week', 'nemtly-booking' ),
	day: __( 'Day', 'nemtly-booking' ),
	list: __( 'List', 'nemtly-booking' ),
};

const DAY_LABELS = [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ];

/**
 * Static month grid preview for the block editor.
 *
 * @param {Object}   props
 * @param {string}   props.defaultView
 * @param {boolean}  props.showToolbar
 * @param {string[]} props.availableViews
 */
function CalendarPreview( { defaultView, showToolbar, availableViews } ) {
	const now = new Date();
	const month = now.toLocaleString( undefined, {
		month: 'long',
		year: 'numeric',
	} );
	const viewLabel = defaultView
		? VIEW_LABELS[ defaultView ] || defaultView
		: __( 'Month', 'nemtly-booking' );
	const views =
		availableViews && availableViews.length ? availableViews : ALL_VIEWS;

	// Build a mini 5-row calendar grid for the current month.
	const firstOfMonth = new Date( now.getFullYear(), now.getMonth(), 1 );
	const startDay = firstOfMonth.getDay();
	const daysInMonth = new Date(
		now.getFullYear(),
		now.getMonth() + 1,
		0
	).getDate();

	const cells = [];
	for ( let i = 0; i < 35; i++ ) {
		const dayNum = i - startDay + 1;
		cells.push( dayNum > 0 && dayNum <= daysInMonth ? dayNum : null );
	}

	return (
		<div
			style={ {
				background: '#f8fafc',
				border: '1px solid #e2e8f0',
				borderRadius: '8px',
				overflow: 'hidden',
				fontSize: '12px',
			} }
		>
			{ /* Toolbar preview */ }
			{ showToolbar !== false && (
				<div
					style={ {
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '8px 12px',
						borderBottom: '1px solid #e2e8f0',
						background: '#fff',
					} }
				>
					<div
						style={ {
							display: 'flex',
							alignItems: 'center',
							gap: '6px',
						} }
					>
						<span style={ { color: '#94a3b8', cursor: 'default' } }>
							‹
						</span>
						<strong
							style={ { color: '#334155', fontSize: '13px' } }
						>
							{ month }
						</strong>
						<span style={ { color: '#94a3b8', cursor: 'default' } }>
							›
						</span>
						<span
							style={ {
								fontSize: '11px',
								padding: '2px 8px',
								border: '1px solid #e2e8f0',
								borderRadius: '4px',
								color: '#64748b',
							} }
						>
							{ __( 'Today', 'nemtly-booking' ) }
						</span>
					</div>

					<div
						style={ {
							display: 'flex',
							borderRadius: '4px',
							overflow: 'hidden',
							border: '1px solid #e2e8f0',
						} }
					>
						{ views.map( ( v ) => (
							<span
								key={ v }
								style={ {
									padding: '2px 8px',
									fontSize: '11px',
									background:
										v === ( defaultView || 'month' )
											? '#334155'
											: '#fff',
									color:
										v === ( defaultView || 'month' )
											? '#fff'
											: '#64748b',
									borderRight: '1px solid #e2e8f0',
								} }
							>
								{ VIEW_LABELS[ v ] || v }
							</span>
						) ) }
					</div>
				</div>
			) }

			{ /* Mini month grid */ }
			<div style={ { padding: '8px' } }>
				<div
					style={ {
						display: 'grid',
						gridTemplateColumns: 'repeat(7, 1fr)',
						gap: '1px',
						textAlign: 'center',
					} }
				>
					{ DAY_LABELS.map( ( d ) => (
						<div
							key={ d }
							style={ {
								color: '#94a3b8',
								fontSize: '10px',
								fontWeight: 600,
								padding: '4px 0',
							} }
						>
							{ d }
						</div>
					) ) }
					{ cells.map( ( dayNum, i ) => {
						const isToday = dayNum === now.getDate();
						return (
							<div
								key={ i }
								style={ {
									padding: '6px 2px',
									minHeight: '28px',
									fontSize: '11px',
									color: dayNum
										? isToday
											? '#fff'
											: '#475569'
										: 'transparent',
									background: isToday
										? '#334155'
										: 'transparent',
									borderRadius: isToday ? '50%' : '0',
									width: isToday ? '24px' : 'auto',
									height: isToday ? '24px' : 'auto',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									margin: 'auto',
								} }
							>
								{ dayNum || '' }
							</div>
						);
					} ) }
				</div>
			</div>

			{ /* Footer label */ }
			<div
				style={ {
					textAlign: 'center',
					padding: '6px',
					color: '#94a3b8',
					fontSize: '11px',
					borderTop: '1px solid #e2e8f0',
					background: '#fff',
				} }
			>
				{ __( 'Event Calendar', 'nemtly-booking' ) }
				{ defaultView
					? ` — ${ viewLabel } ${ __( 'view', 'nemtly-booking' ) }`
					: '' }
			</div>
		</div>
	);
}

/**
 * Edit component for the Event Calendar block.
 *
 * @param {Object}   props
 * @param {Object}   props.attributes
 * @param {Function} props.setAttributes
 */
const FIRST_DAY_OPTIONS = [
	{ label: __( 'Sunday', 'nemtly-booking' ), value: 0 },
	{ label: __( 'Monday', 'nemtly-booking' ), value: 1 },
];

const TIME_FORMAT_OPTIONS = [
	{ label: __( 'Site Default', 'nemtly-booking' ), value: 'site_default' },
	{ label: __( '12-hour (2:30 PM)', 'nemtly-booking' ), value: '12h' },
	{ label: __( '24-hour (14:30)', 'nemtly-booking' ), value: '24h' },
];

const COLOR_BY_OPTIONS = [
	{ label: __( 'Category', 'nemtly-booking' ), value: 'category' },
	{ label: __( 'Event Type', 'nemtly-booking' ), value: 'event_type' },
	{ label: __( 'None', 'nemtly-booking' ), value: 'none' },
];

const HEIGHT_OPTIONS = [
	{ label: __( 'Auto (fit content)', 'nemtly-booking' ), value: 'auto' },
	{ label: __( 'Fixed height', 'nemtly-booking' ), value: 'fixed' },
];

export default function Edit( { attributes, setAttributes } ) {
	const {
		defaultView,
		showToolbar,
		availableViews,
		categories: selectedCats,
		tags: selectedTags,
		eventType,
		firstDayOfWeek,
		eventsPerCell,
		timeFormat,
		showEventTime,
		showBookingBtn,
		showPastEvents,
		colorBy,
		calendarHeight,
		calendarHeightPx,
	} = attributes;
	const blockProps = useBlockProps( {
		className: 'nb-event-calendar-block-preview',
	} );

	const [ allCategories, setAllCategories ] = useState( [] );
	const [ allTags, setAllTags ] = useState( [] );
	const [ loadingTaxes, setLoadingTaxes ] = useState( true );

	// Load categories and tags for the multi-select controls.
	useEffect( () => {
		Promise.all( [
			apiFetch( {
				path: '/wp/v2/nemtly_event_category?per_page=100',
			} ).catch( () => [] ),
			apiFetch( { path: '/wp/v2/nemtly_event_tag?per_page=100' } ).catch(
				() => []
			),
		] ).then( ( [ cats, tags ] ) => {
			setAllCategories( cats || [] );
			setAllTags( tags || [] );
			setLoadingTaxes( false );
		} );
	}, [] );

	const toggleAvailableView = ( view ) => {
		const current = Array.isArray( availableViews )
			? [ ...availableViews ]
			: [];
		const idx = current.indexOf( view );
		if ( idx >= 0 ) {
			if ( current.length <= 1 ) {
				return;
			} // Must keep at least one.
			current.splice( idx, 1 );
		} else {
			current.push( view );
		}
		setAttributes( { availableViews: current } );
	};

	const toggleTaxItem = ( attr, id ) => {
		const current = Array.isArray( attributes[ attr ] )
			? [ ...attributes[ attr ] ]
			: [];
		const idx = current.indexOf( id );
		if ( idx >= 0 ) {
			current.splice( idx, 1 );
		} else {
			current.push( id );
		}
		setAttributes( { [ attr ]: current } );
	};

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Calendar Settings', 'nemtly-booking' ) }
				>
					<SelectControl
						label={ __( 'Default View', 'nemtly-booking' ) }
						value={ defaultView || 'month' }
						options={ VIEW_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { defaultView: value } )
						}
						help={ __(
							'The view the calendar opens in by default.',
							'nemtly-booking'
						) }
					/>

					<fieldset style={ { marginBottom: '16px' } }>
						<legend
							style={ {
								fontSize: '11px',
								fontWeight: 500,
								textTransform: 'uppercase',
								marginBottom: '8px',
							} }
						>
							{ __( 'Available Views', 'nemtly-booking' ) }
						</legend>
						{ ALL_VIEWS.map( ( v ) => (
							<CheckboxControl
								key={ v }
								label={
									v.charAt( 0 ).toUpperCase() + v.slice( 1 )
								}
								checked={
									! availableViews ||
									! availableViews.length ||
									availableViews.includes( v )
								}
								onChange={ () => toggleAvailableView( v ) }
							/>
						) ) }
						<p
							style={ {
								fontSize: '12px',
								color: '#757575',
								marginTop: '4px',
							} }
						>
							{ __(
								'At least one view must be enabled.',
								'nemtly-booking'
							) }
						</p>
					</fieldset>

					<ToggleControl
						label={ __( 'Show Toolbar', 'nemtly-booking' ) }
						checked={ showToolbar !== false }
						onChange={ ( value ) =>
							setAttributes( { showToolbar: value } )
						}
						help={ __(
							'Show the navigation bar and view switcher above the calendar.',
							'nemtly-booking'
						) }
					/>

					<SelectControl
						label={ __( 'Week Starts On', 'nemtly-booking' ) }
						value={ firstDayOfWeek }
						options={ FIRST_DAY_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( {
								firstDayOfWeek: parseInt( value, 10 ),
							} )
						}
						help={ __(
							'First day of the week shown on the calendar.',
							'nemtly-booking'
						) }
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Display Options', 'nemtly-booking' ) }
					initialOpen={ false }
				>
					<SelectControl
						label={ __( 'Time Format', 'nemtly-booking' ) }
						value={ timeFormat }
						options={ TIME_FORMAT_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { timeFormat: value } )
						}
						help={ __(
							'How event times are displayed on the calendar.',
							'nemtly-booking'
						) }
					/>

					<TextControl
						label={ __( 'Events Per Day Cell', 'nemtly-booking' ) }
						type="number"
						min={ 1 }
						max={ 10 }
						value={ eventsPerCell }
						onChange={ ( value ) =>
							setAttributes( {
								eventsPerCell: parseInt( value, 10 ) || 3,
							} )
						}
						help={ __(
							'Maximum events shown per day cell (month view) before "+N more".',
							'nemtly-booking'
						) }
					/>

					<ToggleControl
						label={ __( 'Show Event Time', 'nemtly-booking' ) }
						checked={ showEventTime !== false }
						onChange={ ( value ) =>
							setAttributes( { showEventTime: value } )
						}
						help={ __(
							'Display event times on calendar chips and popovers.',
							'nemtly-booking'
						) }
					/>

					<ToggleControl
						label={ __( 'Show Booking Button', 'nemtly-booking' ) }
						checked={ showBookingBtn !== false }
						onChange={ ( value ) =>
							setAttributes( { showBookingBtn: value } )
						}
						help={ __(
							'Show a "Book Now" button in the event popover and list view.',
							'nemtly-booking'
						) }
					/>

					<ToggleControl
						label={ __( 'Show Past Events', 'nemtly-booking' ) }
						checked={ showPastEvents || false }
						onChange={ ( value ) =>
							setAttributes( { showPastEvents: value } )
						}
						help={ __(
							'When enabled, past events appear on the calendar with reduced opacity.',
							'nemtly-booking'
						) }
					/>

					<SelectControl
						label={ __( 'Color Events By', 'nemtly-booking' ) }
						value={ colorBy }
						options={ COLOR_BY_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { colorBy: value } )
						}
						help={ __(
							'How event chips are color-coded on the calendar.',
							'nemtly-booking'
						) }
					/>

					<SelectControl
						label={ __( 'Calendar Height', 'nemtly-booking' ) }
						value={ calendarHeight }
						options={ HEIGHT_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { calendarHeight: value } )
						}
						help={ __(
							'Auto adjusts to content. Fixed height adds a scrollbar.',
							'nemtly-booking'
						) }
					/>

					{ calendarHeight === 'fixed' && (
						<TextControl
							label={ __(
								'Fixed Height (px)',
								'nemtly-booking'
							) }
							type="number"
							min={ 300 }
							max={ 2000 }
							value={ calendarHeightPx }
							onChange={ ( value ) =>
								setAttributes( {
									calendarHeightPx:
										parseInt( value, 10 ) || 600,
								} )
							}
							help={ __(
								'Height in pixels (300–2000).',
								'nemtly-booking'
							) }
						/>
					) }
				</PanelBody>

				<PanelBody
					title={ __( 'Pre-filter Events', 'nemtly-booking' ) }
					initialOpen={ false }
				>
					{ loadingTaxes && <Spinner /> }

					{ ! loadingTaxes && allCategories.length > 0 && (
						<fieldset style={ { marginBottom: '16px' } }>
							<legend
								style={ {
									fontSize: '11px',
									fontWeight: 500,
									textTransform: 'uppercase',
									marginBottom: '8px',
								} }
							>
								{ __( 'Categories', 'nemtly-booking' ) }
							</legend>
							{ allCategories.map( ( cat ) => (
								<CheckboxControl
									key={ cat.id }
									label={ decodeEntities( cat.name ) }
									checked={
										Array.isArray( selectedCats ) &&
										selectedCats.includes( cat.id )
									}
									onChange={ () =>
										toggleTaxItem( 'categories', cat.id )
									}
								/>
							) ) }
							<p
								style={ {
									fontSize: '12px',
									color: '#757575',
									marginTop: '4px',
								} }
							>
								{ __(
									'Only show events from these categories. Leave empty for all.',
									'nemtly-booking'
								) }
							</p>
						</fieldset>
					) }

					{ ! loadingTaxes && allTags.length > 0 && (
						<fieldset style={ { marginBottom: '16px' } }>
							<legend
								style={ {
									fontSize: '11px',
									fontWeight: 500,
									textTransform: 'uppercase',
									marginBottom: '8px',
								} }
							>
								{ __( 'Tags', 'nemtly-booking' ) }
							</legend>
							{ allTags.map( ( tag ) => (
								<CheckboxControl
									key={ tag.id }
									label={ decodeEntities( tag.name ) }
									checked={
										Array.isArray( selectedTags ) &&
										selectedTags.includes( tag.id )
									}
									onChange={ () =>
										toggleTaxItem( 'tags', tag.id )
									}
								/>
							) ) }
							<p
								style={ {
									fontSize: '12px',
									color: '#757575',
									marginTop: '4px',
								} }
							>
								{ __(
									'Only show events with these tags. Leave empty for all.',
									'nemtly-booking'
								) }
							</p>
						</fieldset>
					) }

					<SelectControl
						label={ __( 'Event Type', 'nemtly-booking' ) }
						value={ eventType || '' }
						options={ EVENT_TYPE_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { eventType: value } )
						}
						help={ __(
							'Limit the calendar to a specific event type.',
							'nemtly-booking'
						) }
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<CalendarPreview
					defaultView={ defaultView }
					showToolbar={ showToolbar }
					availableViews={ availableViews }
				/>
			</div>
		</>
	);
}
